"use client";

import { onAuthStateChanged } from "firebase/auth";
import { fetchAndActivate, getRemoteConfig, getValue } from "firebase/remote-config";
import { ReactNode, useEffect } from "react";

import useFirebaseStore from "hooks/useFirebaseStore";
import useUserStore from "hooks/useUserStore";
import { app, auth } from "lib/firebase.config";

const FirebaseProvider = ({ children }: { children: ReactNode }) => {
  const { setRemoteConfig, setAppReady, setRequestReady, setAuthReady } = useFirebaseStore();
  const { deleteUser } = useUserStore();

  useEffect(() => {
    if (typeof window !== "undefined") {
      setAppReady(false);
      (async () => {
        const remoteConfig = getRemoteConfig(app);
        remoteConfig.defaultConfig = {
          isMaintenanceMode: false,
          maintenanceDateString: "",
        };
        remoteConfig.settings.minimumFetchIntervalMillis = 50000;
        return await fetchAndActivate(remoteConfig)
          .then(() => {
            setRemoteConfig({
              isMaintenanceMode: getValue(remoteConfig, "isMaintenanceMode").asBoolean(),
              maintenanceDateString: getValue(remoteConfig, "maintenanceDateString").asString(),
            });
          })
          .catch(() => null);
      })();
      setAppReady(true);
    }
  }, [setRemoteConfig, setAppReady]);

  useEffect(() => {
    setRequestReady(false);
    const unsubscribed = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Firebase Authからユーザ情報を取得完了
        setAuthReady(true);
        setRequestReady(true);
      } else {
        setAuthReady(true);
        setRequestReady(false);
        deleteUser();
      }
    });
    return () => unsubscribed();
  }, [setRequestReady, deleteUser, setAuthReady]);

  return <>{children}</>;
};

export default FirebaseProvider;
